import { useCallback, useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { createBlog, updateBlog, getAdminBlogs } from "@/lib/api";
import { FileUpload } from "./FileUpload";
import {
  ArrowLeft,
  Bold,
  Heading2,
  Heading3,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Redo,
  Undo,
} from "lucide-react";

const CATEGORIES = [
  "Behind the Scenes",
  "Song Ideas",
  "Planning Tips",
  "Performance Tips",
];

const DEFAULT_AUTHOR = {
  name: "Khusbu Gemmati",
  image:
    "https://cdn.prod.website-files.com/686229256a807206c1874545/687bcf08468fe4993282a9ec_Khusbu.avif",
  bio: "Dancing is my first language! With 15+ years of teaching experience, I created BollyRhythm to make learning fun and stress-free. Whether it's for an event or a living room show, I'm here to help you shine.",
};

type BlogFormData = {
  title: string;
  slug: string;
  category: string;
  published_at: string;
  listing_date: string;
  read_time: string;
  excerpt: string;
  hero_image: string;
  listing_image: string;
  author_name: string;
  author_image: string;
  author_bio: string;
  is_published: boolean;
  sort_order: number;
};

function slugify(text: string) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

type Props = {
  blogId: string | null; // null = new post
  onBack: () => void;
};

export function BlogEditor({ blogId, onBack }: Props) {
  const [form, setForm] = useState<BlogFormData>({
    title: "",
    slug: "",
    category: CATEGORIES[0] as string,
    published_at: "",
    listing_date: "",
    read_time: "5",
    excerpt: "",
    hero_image: "",
    listing_image: "",
    author_name: DEFAULT_AUTHOR.name,
    author_image: DEFAULT_AUTHOR.image,
    author_bio: DEFAULT_AUTHOR.bio,
    is_published: false,
    sort_order: 0,
  });
  const [initialHtml, setInitialHtml] = useState("");
  const [loading, setLoading] = useState(!!blogId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: { openOnClick: false },
      }),
      Image,
    ],
    content: initialHtml,
  });

  // Load existing blog for editing
  useEffect(() => {
    if (!blogId) return;
    let cancelled = false;

    (async () => {
      const { data: blogs } = await getAdminBlogs();
      if (cancelled) return;
      const blog = blogs?.find((b: { id: string }) => b.id === blogId);
      if (!blog) {
        setError("Blog post not found");
        setLoading(false);
        return;
      }
      setForm({
        title: blog.title,
        slug: blog.slug,
        category: blog.category,
        published_at: blog.published_at ?? "",
        listing_date: blog.listing_date ?? "",
        read_time: blog.read_time ?? "",
        excerpt: blog.excerpt ?? "",
        hero_image: blog.hero_image ?? "",
        listing_image: blog.listing_image ?? "",
        author_name: blog.author_name,
        author_image: blog.author_image,
        author_bio: blog.author_bio,
        is_published: blog.is_published,
        sort_order: blog.sort_order,
      });
      setInitialHtml(blog.body_html);
      editor?.commands.setContent(blog.body_html);
      setSlugManuallyEdited(true);
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [blogId, editor]);

  const handleTitleChange = useCallback(
    (title: string) => {
      setForm((prev) => ({
        ...prev,
        title,
        slug: slugManuallyEdited ? prev.slug : slugify(title),
      }));
    },
    [slugManuallyEdited],
  );

  const handleSave = async () => {
    if (!form.title.trim() || !form.slug.trim()) {
      setError("Title and slug are required.");
      return;
    }

    setSaving(true);
    setError(null);

    const bodyHtml = editor?.getHTML() ?? "";
    const payload = {
      ...form,
      body_html: bodyHtml,
      published_at: form.published_at || undefined,
      listing_date: form.listing_date || undefined,
      read_time: form.read_time || undefined,
      excerpt: form.excerpt || undefined,
      hero_image: form.hero_image || undefined,
      listing_image: form.listing_image || undefined,
    };

    let result: { error: { message: string } | null };
    if (blogId) {
      result = await updateBlog(blogId, payload);
    } else {
      result = await createBlog(payload);
    }

    setSaving(false);
    if (result.error) {
      setError(result.error.message);
    } else {
      onBack();
    }
  };

  const addLink = useCallback(() => {
    const url = prompt("Link URL:");
    if (url && editor) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  }, [editor]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-pink-dark border-t-transparent" />
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 transition-colors hover:text-pink-dark"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to list
      </button>

      <h2 className="mb-6 text-xl font-semibold">
        {blogId ? "Edit Post" : "New Post"}
      </h2>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium text-gray-700">Title</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Slug</label>
          <input
            type="text"
            value={form.slug}
            onChange={(e) => {
              setSlugManuallyEdited(true);
              setForm((p) => ({ ...p, slug: e.target.value }));
            }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Category</label>
          <select
            value={form.category}
            onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Published Date (display)
          </label>
          <input
            type="text"
            value={form.published_at}
            onChange={(e) => setForm((p) => ({ ...p, published_at: e.target.value }))}
            placeholder="March 11, 2026"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Listing Date (short)
          </label>
          <input
            type="text"
            value={form.listing_date}
            onChange={(e) => setForm((p) => ({ ...p, listing_date: e.target.value }))}
            placeholder="11 Mar"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Read Time</label>
          <input
            type="text"
            value={form.read_time}
            onChange={(e) => setForm((p) => ({ ...p, read_time: e.target.value }))}
            placeholder="5"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium text-gray-700">Excerpt</label>
          <textarea
            value={form.excerpt}
            onChange={(e) => setForm((p) => ({ ...p, excerpt: e.target.value }))}
            rows={2}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Hero Image</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={form.hero_image}
              onChange={(e) => setForm((p) => ({ ...p, hero_image: e.target.value }))}
              className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
              placeholder="https://..."
            />
            <FileUpload
              folder="blog-images"
              accept="image/*"
              label="Upload"
              onUploaded={(url) => setForm((p) => ({ ...p, hero_image: url }))}
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Listing Image</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={form.listing_image}
              onChange={(e) => setForm((p) => ({ ...p, listing_image: e.target.value }))}
              className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
              placeholder="https://..."
            />
            <FileUpload
              folder="blog-images"
              accept="image/*"
              label="Upload"
              onUploaded={(url) => setForm((p) => ({ ...p, listing_image: url }))}
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Author Name</label>
          <input
            type="text"
            value={form.author_name}
            onChange={(e) => setForm((p) => ({ ...p, author_name: e.target.value }))}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Author Image URL</label>
          <input
            type="text"
            value={form.author_image}
            onChange={(e) => setForm((p) => ({ ...p, author_image: e.target.value }))}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium text-gray-700">Author Bio</label>
          <textarea
            value={form.author_bio}
            onChange={(e) => setForm((p) => ({ ...p, author_bio: e.target.value }))}
            rows={2}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Sort Order</label>
          <input
            type="number"
            value={form.sort_order}
            onChange={(e) =>
              setForm((p) => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))
            }
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
          />
          <p className="mt-1 text-xs text-gray-500">
            Lowest value appears first. The first post is featured on the blog page.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is-published"
            checked={form.is_published}
            onChange={(e) => setForm((p) => ({ ...p, is_published: e.target.checked }))}
            className="h-4 w-4 rounded border-gray-300 text-pink-dark focus:ring-pink-dark"
          />
          <label htmlFor="is-published" className="text-sm font-medium text-gray-700">
            Published
          </label>
        </div>
      </div>

      {/* TipTap Editor */}
      <div className="mt-6">
        <label className="mb-2 block text-sm font-medium text-gray-700">Body</label>
        <div className="overflow-hidden rounded-xl border border-gray-300 focus-within:border-pink-dark focus-within:ring-1 focus-within:ring-pink-dark">
          {/* Toolbar */}
          <div className="flex flex-wrap gap-1 border-b border-gray-200 bg-gray-50 px-2 py-1.5">
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleBold().run()}
              active={editor?.isActive("bold")}
              title="Bold"
            >
              <Bold className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              active={editor?.isActive("italic")}
              title="Italic"
            >
              <Italic className="h-4 w-4" />
            </ToolbarButton>
            <div className="mx-1 h-6 w-px bg-gray-300" />
            <ToolbarButton
              onClick={() =>
                editor?.chain().focus().toggleHeading({ level: 2 }).run()
              }
              active={editor?.isActive("heading", { level: 2 })}
              title="Heading 2"
            >
              <Heading2 className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() =>
                editor?.chain().focus().toggleHeading({ level: 3 }).run()
              }
              active={editor?.isActive("heading", { level: 3 })}
              title="Heading 3"
            >
              <Heading3 className="h-4 w-4" />
            </ToolbarButton>
            <div className="mx-1 h-6 w-px bg-gray-300" />
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
              active={editor?.isActive("bulletList")}
              title="Bullet List"
            >
              <List className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleOrderedList().run()}
              active={editor?.isActive("orderedList")}
              title="Ordered List"
            >
              <ListOrdered className="h-4 w-4" />
            </ToolbarButton>
            <div className="mx-1 h-6 w-px bg-gray-300" />
            <ToolbarButton onClick={addLink} active={editor?.isActive("link")} title="Link">
              <LinkIcon className="h-4 w-4" />
            </ToolbarButton>
            <FileUpload
              folder="blog-images"
              accept="image/*"
              label="Upload image"
              onUploaded={(url) => {
                editor?.chain().focus().setImage({ src: url }).run();
              }}
            />
            <div className="mx-1 h-6 w-px bg-gray-300" />
            <ToolbarButton
              onClick={() => editor?.chain().focus().undo().run()}
              title="Undo"
            >
              <Undo className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor?.chain().focus().redo().run()}
              title="Redo"
            >
              <Redo className="h-4 w-4" />
            </ToolbarButton>
          </div>

          {/* Editor area */}
          <EditorContent
            editor={editor}
            className="max-w-none px-4 py-3 [&_.ProseMirror]:min-h-[20rem] [&_.ProseMirror]:outline-none"
          />
        </div>
      </div>

      <div className="mt-6 flex gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded-xl bg-pink-dark px-6 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-px active:scale-95 disabled:opacity-50"
        >
          {saving ? "Saving…" : blogId ? "Update Post" : "Create Post"}
        </button>
        <button
          type="button"
          onClick={onBack}
          className="rounded-xl border border-gray-300 px-6 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function ToolbarButton({
  onClick,
  active,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`rounded p-1.5 transition-colors ${
        active
          ? "bg-pink-dark/10 text-pink-dark"
          : "text-gray-600 hover:bg-gray-200 hover:text-gray-900"
      }`}
    >
      {children}
    </button>
  );
}
